import AddressPredictionsViewCmpDesktop from '@porterin/booking-flow/src/order-review/view/components/AddressPredictionsViewCmpDesktop';
import WaypointIcon from '@porterin/booking-flow/src/order-review/view/components/WaypointIcon';
import { OrderReviewLabels } from '@porterin/booking-flow/src/order-review/view/stores/OrderReviewStore';
import { AddressType } from '@porterin/booking-flow/src/order-review/view/types/OrderReviewAddressSchema';
import { TextInput } from '@porterin/component-library/src/base-components';
import IconTitleCmp from '@porterin/component-library/src/composite-components/icon-title/IconTitleCmp';
import { useLocation } from '@porterin/component-library/src/location-accuracy/contexts/LocationContext';
import {
  backArrowIcon,
  detectingLocationGifUrl,
  detectingLocationTitle,
} from '@porterin/component-library/src/location-accuracy/store/LocationAccuracyStore';
import React, { useEffect, useState } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import { MapLocationSelectionCmpDesktop } from './MapLocationSelectionCmpDesktop';
import { AutoDetectLocationCmpDesktop } from './AutoDetectLocationCmpDesktop';
import Image from 'next/image';
import { AutoDetectLocationErrorCmpDesktop } from './AutoDetectLocationErrorCmpDesktop';
import { useRouter } from 'next/router';

export interface SelectLocationProps {
  close_modal: () => void;
  predictions: google.maps.places.AutocompletePrediction[];
  on_prediction_click: (
    prediction: google.maps.places.AutocompletePrediction,
  ) => void;
  error: string | undefined;
  register: UseFormRegisterReturn<AddressType>;
}

export const SelectLocationViewCmpDesktop: React.FC<SelectLocationProps> = ({
  close_modal,
  predictions,
  on_prediction_click,
  error,
  register,
}) => {
  const { state, updateStep } = useLocation();
  const [isLocationDetecting, setIsLocationDetecting] =
    useState<boolean>(false);
  const [detectLocationError, setDetectLocationError] = useState<string | null>(
    null,
  );
  const [inputValue, setInputValue] = useState<string>(
    state.address?.landmark ?? '',
  );
  const router = useRouter();
  const editLocation =
    router?.pathname.includes('fare-estimate') ||
    router?.pathname.includes('order-review');

  const focusTextInput = () => {
    const inputElement = document.querySelector(
      '[data-testid="custom-address-edit"]',
    ) as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
    }
  };

  useEffect(() => {
    if (state.address?.landmark !== undefined) {
      setInputValue(state.address.landmark);
    }
  }, [state.address?.landmark]);

  return (
    <div className="overflow-hidden h-full">
      <div className="flex flex-col bg-white sticky top-0">
        {!editLocation && (
          <IconTitleCmp
            icon={backArrowIcon}
            onIconClick={() => close_modal()}
            text={`${OrderReviewLabels.waypointPrefix} ${state.waypoint}?`}
          />
        )}
        <form className="w-full mt-[1rem]">
          <TextInput
            data-testid="custom-address-edit"
            identifiers={{ id: 'custom-address-edit' }}
            inputOptions={{
              placeholder: OrderReviewLabels.addressInputPlaceholder,
              error: error,
            }}
            autoFocus={true}
            icons={{ leftIcon: <WaypointIcon waypoint={state.waypoint} /> }}
            {...register}
            onChange={e => {
              register.onChange(e);
              setDetectLocationError(null);
              setIsLocationDetecting(false);
              updateStep('SelectLocation');
              const newValue = e.target.value;
              setInputValue(newValue);
            }}
            value={inputValue}
          />
        </form>
        {state.step === 'SelectLocation' && (
          <AutoDetectLocationCmpDesktop
            setIsLoading={setIsLocationDetecting}
            isLoading={isLocationDetecting}
            setError={setDetectLocationError}
            error={detectLocationError}
          />
        )}
      </div>
      <div className="w-full h-full">
        {state.step === 'SelectLocation' ? (
          (() => {
            if (detectLocationError) {
              return (
                <div className="w-full h-[65%] flex flex-col items-center justify-center">
                  <AutoDetectLocationErrorCmpDesktop
                    message={detectLocationError}
                    onSearchClick={focusTextInput}
                  />
                </div>
              );
            }
            if (isLocationDetecting) {
              return (
                <div
                  className={`w-full h-[65%] flex flex-col items-center justify-center ${editLocation ? 'mt-0' : 'mt-[6rem]'}`}
                >
                  <Image
                    src={detectingLocationGifUrl}
                    alt="fetch place"
                    width={177}
                    height={105}
                    priority={true}
                  />
                  <div className="text-center text-[1.4rem] font-semibold mt-[1.6rem] text-[#1B2531]">
                    {detectingLocationTitle}
                  </div>
                </div>
              );
            }
            return (
              <div
                className={`w-full ${editLocation ? 'h-[75%]' : 'h-[65%]'} flex flex-col items-center justify-center`}
              >
                <AddressPredictionsViewCmpDesktop
                  predictions={predictions}
                  on_prediction_click={on_prediction_click}
                />
              </div>
            );
          })()
        ) : (
          <div
            data-testid="map-location-wrapper"
            className="w-full h-full overflow-y-scroll box-border bg-white z-[200] relative"
          >
            <MapLocationSelectionCmpDesktop />
          </div>
        )}
      </div>
    </div>
  );
};
